/**
 * Product Customization Overlay Component
 * Allows users to customize their order with ingredients, accompaniments, and notes
 * Wrapped with React.memo for performance
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Product, CartItemCustomizations } from '../../types/menu';
import { useToast } from '../ui/Toast';

interface CustomizationOverlayProps {
    product: Product;
    currency: string;
    onClose: () => void;
    onAddToCart: (customizations: CartItemCustomizations) => void;
}

const CustomizationOverlay: React.FC<CustomizationOverlayProps> = React.memo(({
    product,
    currency,
    onClose,
    onAddToCart
}) => {
    const { t } = useTranslation();
    const { showToast } = useToast();
    const [removedIngredients, setRemovedIngredients] = useState<string[]>([]);
    const [selectedAccompaniment, setSelectedAccompaniment] = useState<string>('');
    const [specialNotes, setSpecialNotes] = useState<string>('');
    const [isVisible, setIsVisible] = useState(false);
    const [accompanimentError, setAccompanimentError] = useState(false);

    // Parse ingredients and accompaniments if they're strings
    const ingredients: string[] = product.ingredients
        ? (typeof product.ingredients === 'string' ? JSON.parse(product.ingredients) : product.ingredients)
        : [];
    const accompaniments: string[] = product.accompaniments
        ? (typeof product.accompaniments === 'string' ? JSON.parse(product.accompaniments) : product.accompaniments)
        : [];

    useEffect(() => {
        // Trigger slide up animation
        setTimeout(() => setIsVisible(true), 10);
    }, []);

    const handleClose = () => {
        setIsVisible(false);
        setTimeout(onClose, 300);
    };

    const handleAddToCart = () => {
        // Validate if accompaniment is required
        if (product.requires_accompaniment && !selectedAccompaniment) {
            setAccompanimentError(true);
            showToast('Please select an accompaniment', 'warning');
            return;
        }

        onAddToCart({
            removed_ingredients: removedIngredients.length > 0 ? removedIngredients : undefined,
            selected_accompaniment: selectedAccompaniment || undefined,
            special_notes: specialNotes.trim() || undefined
        });
    };

    const toggleIngredient = (ingredient: string) => {
        if (removedIngredients.includes(ingredient)) {
            setRemovedIngredients(removedIngredients.filter(i => i !== ingredient));
        } else {
            setRemovedIngredients([...removedIngredients, ingredient]);
        }
    };

    return (
        <div
            style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0, 0, 0, 0.8)',
                backdropFilter: 'blur(10px)',
                zIndex: 150,
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'center'
            }}
            onClick={handleClose}
        >
            <div
                className="glass-dark cart-modal"
                style={{
                    width: '100%',
                    maxWidth: '600px',
                    maxHeight: '80vh',
                    borderRadius: '1.5rem 1.5rem 0 0',
                    padding: '1.5rem',
                    overflowY: 'auto',
                    transform: isVisible ? 'translateY(0)' : 'translateY(100%)',
                    transition: 'transform 0.3s ease-out'
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '1.5rem',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                    paddingBottom: '1rem'
                }}>
                    <div>
                        <h2 style={{ color: 'white', fontSize: '1.5rem', fontWeight: 'bold' }}>
                            Customize Your Order
                        </h2>
                        <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                            {product.name}
                        </p>
                    </div>
                    <button
                        onClick={handleClose}
                        aria-label="Close customization"
                        style={{
                            background: 'rgba(255, 255, 255, 0.1)',
                            border: 'none',
                            borderRadius: '50%',
                            width: '2.5rem',
                            height: '2.5rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            color: 'white',
                            fontSize: '1.5rem'
                        }}
                    >
                        ×
                    </button>
                </div>

                {/* Ingredients Section */}
                {ingredients.length > 0 && (
                    <div style={{ marginBottom: '1.5rem' }}>
                        <h3 style={{
                            color: 'white',
                            fontSize: '1.125rem',
                            fontWeight: '600',
                            marginBottom: '1rem'
                        }}>
                            🍴 Remove Ingredients
                        </h3>
                        <p style={{
                            color: 'rgba(255, 255, 255, 0.6)',
                            fontSize: '0.875rem',
                            marginBottom: '1rem'
                        }}>
                            Select ingredients you'd like to remove
                        </p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                            {ingredients.map((ingredient) => (
                                <label
                                    key={ingredient}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        padding: '0.75rem',
                                        background: removedIngredients.includes(ingredient)
                                            ? 'rgba(255, 0, 0, 0.2)'
                                            : 'rgba(255, 255, 255, 0.05)',
                                        borderRadius: '0.5rem',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s'
                                    }}
                                >
                                    <input
                                        type="checkbox"
                                        checked={removedIngredients.includes(ingredient)}
                                        onChange={() => toggleIngredient(ingredient)}
                                        style={{
                                            width: '1.25rem',
                                            height: '1.25rem',
                                            marginRight: '0.75rem',
                                            cursor: 'pointer'
                                        }}
                                    />
                                    <span style={{
                                        color: 'white',
                                        fontSize: '1rem',
                                        textDecoration: removedIngredients.includes(ingredient) ? 'line-through' : 'none'
                                    }}>
                                        {ingredient}
                                    </span>
                                </label>
                            ))}
                        </div>
                    </div>
                )}

                {/* Accompaniments Section */}
                {accompaniments.length > 0 && (
                    <div style={{ marginBottom: '1.5rem' }}>
                        <h3 style={{
                            color: 'white',
                            fontSize: '1.125rem',
                            fontWeight: '600',
                            marginBottom: '1rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem'
                        }}>
                            🍛 Choose Accompaniment
                            {product.requires_accompaniment && (
                                <span style={{
                                    color: '#FF6B6B',
                                    fontSize: '0.75rem',
                                    background: 'rgba(255, 107, 107, 0.2)',
                                    padding: '0.25rem 0.5rem',
                                    borderRadius: '0.25rem'
                                }}>
                                    Required
                                </span>
                            )}
                        </h3>
                        <p style={{
                            color: accompanimentError ? '#ef4444' : 'rgba(255, 255, 255, 0.6)',
                            fontSize: '0.875rem',
                            marginBottom: '1rem'
                        }}>
                            {accompanimentError
                                ? '⚠ Please select an accompaniment'
                                : product.requires_accompaniment
                                    ? 'Choose one accompaniment (required)'
                                    : 'Choose an accompaniment (optional)'}
                        </p>
                        <div
                            role="radiogroup"
                            aria-label="Accompaniment selection"
                            aria-required={product.requires_accompaniment}
                            style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}
                        >
                            {accompaniments.map((accompaniment) => (
                                <label
                                    key={accompaniment}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        padding: '0.75rem',
                                        background: selectedAccompaniment === accompaniment
                                            ? 'rgba(139, 0, 0, 0.4)'
                                            : accompanimentError
                                                ? 'rgba(239, 68, 68, 0.1)'
                                                : 'rgba(255, 255, 255, 0.05)',
                                        borderRadius: '0.5rem',
                                        cursor: 'pointer',
                                        border: selectedAccompaniment === accompaniment
                                            ? '2px solid rgba(139, 0, 0, 0.8)'
                                            : accompanimentError
                                                ? '2px solid rgba(239, 68, 68, 0.5)'
                                                : '2px solid transparent',
                                        transition: 'all 0.2s'
                                    }}
                                >
                                    <input
                                        type="radio"
                                        name="accompaniment"
                                        value={accompaniment}
                                        checked={selectedAccompaniment === accompaniment}
                                        onChange={(e) => {
                                            setSelectedAccompaniment(e.target.value);
                                            setAccompanimentError(false);
                                        }}
                                        style={{
                                            width: '1.25rem',
                                            height: '1.25rem',
                                            marginRight: '0.75rem',
                                            cursor: 'pointer'
                                        }}
                                    />
                                    <span style={{ color: 'white', fontSize: '1rem' }}>
                                        {accompaniment}
                                    </span>
                                </label>
                            ))}
                        </div>
                    </div>
                )}

                {/* Special Notes */}
                <div style={{ marginBottom: '1.5rem' }}>
                    <h3 style={{
                        color: 'white',
                        fontSize: '1.125rem',
                        fontWeight: '600',
                        marginBottom: '1rem'
                    }}>
                        💬 Special Notes
                    </h3>
                    <p style={{
                        color: 'rgba(255, 255, 255, 0.6)',
                        fontSize: '0.875rem',
                        marginBottom: '1rem'
                    }}>
                        Any special requests or allergies?
                    </p>
                    <textarea
                        value={specialNotes}
                        onChange={(e) => setSpecialNotes(e.target.value)}
                        placeholder="e.g., Extra spicy, no salt..."
                        style={{
                            width: '100%',
                            padding: '0.75rem',
                            background: 'rgba(255, 255, 255, 0.05)',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            borderRadius: '0.5rem',
                            color: 'white',
                            fontSize: '1rem',
                            resize: 'vertical',
                            minHeight: '80px',
                            fontFamily: 'inherit'
                        }}
                    />
                </div>

                {/* Add to Cart Button */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    paddingTop: '1rem',
                    borderTop: '1px solid rgba(255, 255, 255, 0.1)'
                }}>
                    <div style={{ flex: 1 }}>
                        <div style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.875rem' }}>
                            Price
                        </div>
                        <div style={{ color: '#FFD700', fontSize: '1.5rem', fontWeight: 'bold' }}>
                            {Math.round(Number(product.price))} {currency}
                        </div>
                    </div>
                    <button
                        onClick={handleAddToCart}
                        style={{
                            padding: '1rem 2rem',
                            borderRadius: '0.75rem',
                            border: 'none',
                            background: 'rgba(139, 0, 0, 0.9)',
                            color: 'white',
                            fontSize: '1.125rem',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            transition: 'all 0.3s',
                            whiteSpace: 'nowrap'
                        }}
                        onMouseOver={(e) => e.currentTarget.style.background = 'rgba(139, 0, 0, 1)'}
                        onMouseOut={(e) => e.currentTarget.style.background = 'rgba(139, 0, 0, 0.9)'}
                    >
                        {t('product.addToCart')}
                    </button>
                </div>
            </div>
        </div>
    );
});

CustomizationOverlay.displayName = 'CustomizationOverlay';

export default CustomizationOverlay;
